import React, { useEffect, useState, useCallback } from 'react';
import { Project } from '../../types';
import { dossierAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { LockBar } from '../dossier/LockBar';
import { useSectionClear } from '../dossier/useSectionClear';
import { RichText, htmlHasText } from '../common/RichText';
import { useIsMobile } from '../../hooks/useIsMobile';
import { BRAND, faseKleur } from '../../styles/brand';
import { FaseTag } from '../flow/FaseTag';
import { PARTICIPATIE, normalizeParticipatie } from '../../constants/participatie';

interface StrategieFaseModuleProps {
  project: Project;
}

type ContentKey =
  | 'diagnoseKern' | 'diagnoseOpgave' | 'diagnoseFunctie' | 'diagnoseSpanning'
  | 'modelKeuze' | 'modelVerantwoording' | 'modelBeperkingen'
  | 'vrVisie' | 'vrInterneSituatie' | 'vrExterneSituatie' | 'vrAmbitie' | 'vrStakeholders' | 'vrAanpak' | 'vrAccountability' | 'vrResources'
  | 'miOrganisatievraag' | 'miCommunicatievraag' | 'miSwot' | 'miDoelgroepenStakeholders'
  | 'miPositionering' | 'miPropositie' | 'miDoelstellingen' | 'miKernboodschapStrategie'
  | 'miCreatiefConcept' | 'miMiddelenMedia' | 'miContent' | 'miPlanningBudget'
  | 'hyVerbindendeLijn'
  | 'narratief'
  | 'participatieAanpak' | 'communicatieafspraken'
  | 'risicosKansen' | 'monitoringEvaluatie';

const META: Record<ContentKey, { label: string; hint: string }> = {
  diagnoseKern: { label: 'Kern van het communicatievraagstuk', hint: 'Wat moet communicatie in dit project helpen oplossen of realiseren?' },
  diagnoseOpgave: { label: 'Strategische communicatieopgave', hint: 'Welke bijdrage levert communicatie aan de project- en organisatiedoelen? Wat kan communicatie niet oplossen?' },
  diagnoseFunctie: { label: 'Dominante communicatiefunctie', hint: 'Bijv. informeren, duiden, betekenis geven, verbinden, legitimeren, positioneren, luisteren, samenwerken, activeren, gedrag beïnvloeden, vertrouwen.' },
  diagnoseSpanning: { label: 'Belangrijkste strategische spanning', hint: 'Het voornaamste dilemma, bijv. snelheid vs. zorgvuldigheid of zenden vs. dialoog.' },
  modelKeuze: { label: 'Strategisch communicatiemodel', hint: 'Onderbouwde keuze op basis van de diagnose. De bouwstenen hieronder passen zich aan op je keuze.' },
  modelVerantwoording: { label: 'Verantwoording modelkeuze', hint: 'Waarom past dit model bij de diagnose en waarom het andere minder? Bij hybride: functie van elk model.' },
  modelBeperkingen: { label: 'Beperkingen & aandachtspunten', hint: 'Welke beperkingen brengt de gekozen aanpak mee?' },
  // Van Ruler — Strategisch Communicatie Frame (SCF): 8 bouwstenen
  vrVisie: { label: 'Visie', hint: 'Hoe kijkt de organisatie naar dit vraagstuk en de rol van communicatie? Het overkoepelende beeld en uitgangspunt.' },
  vrInterneSituatie: { label: 'Interne situatie', hint: 'Wat speelt er binnen de organisatie/het project t.a.v. de communicatieopgave (sterktes, zwaktes, draagvlak, capaciteit)?' },
  vrExterneSituatie: { label: 'Externe situatie', hint: 'Relevante ontwikkelingen, verhoudingen en kansen/bedreigingen in de omgeving.' },
  vrAmbitie: { label: 'Ambitie', hint: 'Wat wil je met communicatie bereiken? De gewenste situatie/het streven.' },
  vrStakeholders: { label: 'Stakeholders', hint: 'De relevante betrokkenen en de gewenste relatie en positie t.o.v. hen (bouwt voort op de omgevingsanalyse).' },
  vrAanpak: { label: 'Aanpak', hint: 'De strategische lijn en aanpak om de ambitie te realiseren.' },
  vrAccountability: { label: 'Accountability', hint: 'Hoe verantwoord je de aanpak: welke effecten beoog je, hoe monitor/meet je en leg je verantwoording af?' },
  vrResources: { label: 'Resources', hint: 'Welke mensen, middelen, tijd en budget zijn nodig en beschikbaar?' },
  // Michels — Communicatiemodel: Blok 1 · Analyse
  miOrganisatievraag: { label: 'Organisatievraag', hint: 'Wat wil de organisatie bereiken en wat is het grotere doel?' },
  miCommunicatievraag: { label: 'Communicatievraag', hint: 'Welk communicatieprobleem of -doel hoort daarbij?' },
  miSwot: { label: 'Interne/externe analyse (SWOT)', hint: 'Sterktes, zwaktes, kansen en bedreigingen.' },
  miDoelgroepenStakeholders: { label: 'Doelgroepen & stakeholders', hint: 'Om wie gaat het en wie moeten we bereiken? Bouwt voort op de omgevingsanalyse (geen nieuwe analyse).' },
  // Michels — Blok 2 · Strategie
  miPositionering: { label: 'Positionering', hint: 'Hoe wil de organisatie/het project gezien worden in de omgeving?' },
  miPropositie: { label: 'Propositie', hint: 'Wat is de kernwaarde of belofte aan de doelgroep?' },
  miDoelstellingen: { label: 'Doelstellingen', hint: 'De concrete, meetbare vertaling van de strategische communicatieopgave naar toetsbare doelen (op kennis/houding/gedrag) — bewijsbaar, niet in algemene termen.' },
  miKernboodschapStrategie: { label: 'Kernboodschap / strategie', hint: 'Wat is het centrale verhaal dat je vertelt en de gekozen communicatiestrategie?' },
  // Michels — Blok 3 · Aanpak
  miCreatiefConcept: { label: 'Creatief concept', hint: 'Hoe vertaal je de strategie naar een opvallend en pakkend idee?' },
  miMiddelenMedia: { label: 'Middelen & media (online · offline)', hint: 'Via welke kanalen breng je de boodschap over? Orden waar passend naar PESO — paid, owned, earned — met concrete suggesties.' },
  miContent: { label: 'Content', hint: 'Welke inhoud/content zet je per fase in (bijv. via See–Think–Do–Care)?' },
  miPlanningBudget: { label: 'Planning & budget', hint: 'Wanneer doe je wat en wat mag het kosten?' },
  // Hybride
  hyVerbindendeLijn: { label: 'Verbindende strategische lijn (Van Ruler ↔ Michels)', hint: 'Waarom is de combinatie nodig en hoe vormen beide modellen samen één geheel?' },
  // Gedeeld
  narratief: { label: 'Communicatienarratief', hint: 'Overkoepelend verhaal o.b.v. context, kansen en risico’s: aanleiding/noodzaak, eerlijk over impact en onzekerheid, betekenis en handelingsperspectief. Dit is de basis die je meeneemt naar Fase 5 – Kernboodschap (werk hier nog geen concrete boodschappen uit).' },
  participatieAanpak: { label: 'Relatie met participatie', hint: 'Hoe versterken communicatie en participatie elkaar (voor/tijdens/na, terugkoppeling, verwachtingenmanagement)?' },
  communicatieafspraken: { label: 'Communicatieafspraken', hint: 'Reeds gemaakte afspraken uit de documenten: rollen/afzenders, woordvoering, vaste momenten/ritmes, kanalen, do’s en don’ts.' },
  risicosKansen: { label: 'Communicatieve risico’s & kansen', hint: 'Belangrijkste risico’s en kansen: oorzaak, impact, eerste signalen, maatregelen/vervolgstap.' },
  monitoringEvaluatie: { label: 'Monitoring, evaluatie & bijsturing', hint: 'Welke signalen/bronnen volg je, hoe vaak, wie is verantwoordelijk, en wat zijn de criteria voor bijsturing?' },
};

const ALL_KEYS = Object.keys(META) as ContentKey[];
const emptyContent = ALL_KEYS.reduce((acc, k) => { acc[k] = ''; return acc; }, {} as Record<ContentKey, string>);

const DIAGNOSE: ContentKey[] = ['diagnoseKern', 'diagnoseOpgave', 'diagnoseFunctie', 'diagnoseSpanning'];
const VR: ContentKey[] = ['vrVisie', 'vrInterneSituatie', 'vrExterneSituatie', 'vrAmbitie', 'vrStakeholders', 'vrAanpak', 'vrAccountability', 'vrResources'];
const MI_ANALYSE: ContentKey[] = ['miOrganisatievraag', 'miCommunicatievraag', 'miSwot', 'miDoelgroepenStakeholders'];
const MI_STRATEGIE: ContentKey[] = ['miPositionering', 'miPropositie', 'miDoelstellingen', 'miKernboodschapStrategie'];
const MI_AANPAK: ContentKey[] = ['miCreatiefConcept', 'miMiddelenMedia', 'miContent', 'miPlanningBudget'];
const SHARED_NARRATIEF: ContentKey[] = ['narratief'];
const SHARED_PARTICIPATIE: ContentKey[] = ['participatieAanpak', 'communicatieafspraken'];
const SHARED_RISICO: ContentKey[] = ['risicosKansen', 'monitoringEvaluatie'];

const MODEL_OPTS = [
  { v: 'van-ruler', l: 'Van Ruler — Strategisch Communicatie Frame (strategisch, relationeel, adaptief)' },
  { v: 'michels', l: 'Michels — communicatiemodel (doelen, doelgroepen, boodschappen, middelen)' },
  { v: 'hybride', l: 'Hybride — combinatie van beide modellen' },
];

interface Spoor { naam: string; doel: string; betrokkenen: string; gewensteBeweging: string; kernAanpak: string; risicos: string; }
const emptySpoor = (): Spoor => ({ naam: '', doel: '', betrokkenen: '', gewensteBeweging: '', kernAanpak: '', risicos: '' });
interface Fase { fase: string; opgave: string; mijlpalen: string; aanpak: string; risicos: string; }
const emptyFase = (): Fase => ({ fase: '', opgave: '', mijlpalen: '', aanpak: '', risicos: '' });

interface Doelgroep { personaId: string; persona: string; doel: string; benadering: string; participatieniveau: string; middelen: string; }
// Bestaande doelgroepen bijwerken naar de Factor C-ladder (oude IAP2-treden worden omgezet).
const normDoelgroep = (d: any): Doelgroep => ({ ...d, participatieniveau: normalizeParticipatie(d?.participatieniveau) });

const cardStyle: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, background: '#fff' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 14, fontWeight: 600, color: '#1a1a2e', marginBottom: 2 };
const hintStyle: React.CSSProperties = { fontSize: 12.5, color: '#8a90a0', marginBottom: 10 };
function textareaStyle(disabled: boolean, minHeight = 56): React.CSSProperties {
  return { width: '100%', minHeight, resize: 'vertical', padding: '10px 12px', borderRadius: 8, border: '1px solid #d8dce4', fontSize: 14, lineHeight: 1.55, fontFamily: 'inherit', color: '#20242e', background: disabled ? '#f6f7f9' : '#fff', boxSizing: 'border-box' };
}
function selectStyle(disabled: boolean): React.CSSProperties {
  return { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid #d8dce4', fontSize: 14, fontFamily: 'inherit', color: '#20242e', background: disabled ? '#f6f7f9' : '#fff', boxSizing: 'border-box' };
}
const sectionTitle: React.CSSProperties = { fontSize: 16, fontWeight: 800, color: '#1a1a2e', margin: '6px 0 12px' };

export const StrategieFaseModule: React.FC<StrategieFaseModuleProps> = ({ project }) => {
  const toast = useToast();
  const isMobile = useIsMobile();
  const [content, setContent] = useState<Record<ContentKey, string>>(emptyContent);
  const [sporen, setSporen] = useState<Spoor[]>([]);
  const [fasering, setFasering] = useState<Fase[]>([]);
  const [doelgroepen, setDoelgroepen] = useState<Doelgroep[]>([]);
  const [status, setStatus] = useState<'open' | 'locked'>('open');
  const [lockedAt, setLockedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locking, setLocking] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const locked = status === 'locked';

  const loadDossier = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dossierAPI.getByProject(project.id);
      const section = res?.dossier?.sections?.strategie;
      if (section) {
        const sc: any = section.content || {};
        const { doelgroepen: dg, communicatiesporen: cs, fasering: fs, ...fields } = sc;
        setContent({ ...emptyContent, ...fields });
        setSporen(Array.isArray(cs) ? cs : []);
        setFasering(Array.isArray(fs) ? fs : []);
        setDoelgroepen(Array.isArray(dg) ? dg.map(normDoelgroep) : []);
        setStatus(section.status === 'locked' ? 'locked' : 'open');
        setLockedAt(section.lockedAt || null);
      }
    } catch (e) {
      // dossier wordt server-side aangemaakt zodra beschikbaar
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { loadDossier(); }, [loadDossier]);

  // Beheerders kunnen deze fase leegmaken om in een testsessie opnieuw te vullen.
  const { canClear, clearing, doClear } = useSectionClear(project.id, project.name, 'strategie', "Communicatiestrategie", {}, loadDossier);

  const update = (key: ContentKey, value: string) => setContent((c) => ({ ...c, [key]: value }));
  const payload = () => ({ ...content, communicatiesporen: sporen, fasering, doelgroepen });
  const updateDoelgroep = (idx: number, key: keyof Doelgroep, value: string) => setDoelgroepen((l) => l.map((d, i) => (i === idx ? { ...d, [key]: value } : d)));
  const updateSpoor = (idx: number, key: keyof Spoor, value: string) => setSporen((l) => l.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
  const updateFase = (idx: number, key: keyof Fase, value: string) => setFasering((l) => l.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));

  const saveDraft = async () => {
    setSaving(true);
    try {
      await dossierAPI.saveSection(project.id, 'strategie', payload());
      toast.success('Concept opgeslagen', 'De communicatiestrategie is als concept vastgelegd.');
    } catch (e: any) {
      toast.error('Opslaan mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setSaving(false); }
  };

  const runAssist = async () => {
    setAiLoading(true);
    try {
      // Jouw modelkeuze gaat mee: de AI vult dan de bouwstenen van dát model en verantwoordt dát model.
      const res = await dossierAPI.assistStrategie(project.id, content.modelKeuze);
      const strategie = res?.strategie || {};
      let filled = 0;
      setContent((c) => {
        const next = { ...c };
        ALL_KEYS.forEach((k) => {
          if (!next[k]?.trim() && typeof strategie[k] === 'string' && strategie[k].trim()) { next[k] = strategie[k]; filled++; }
        });
        return next;
      });
      const cs = Array.isArray(strategie.communicatiesporen) ? strategie.communicatiesporen : [];
      if (cs.length) setSporen((cur) => (cur.length ? cur : cs));
      const fs = Array.isArray(strategie.fasering) ? strategie.fasering : [];
      if (fs.length) setFasering((cur) => (cur.length ? cur : fs));
      const dg = Array.isArray(res?.doelgroepen) ? res.doelgroepen : [];
      if (dg.length) {
        setDoelgroepen((cur) => {
          const have = new Set(cur.map((d) => d.personaId).filter(Boolean));
          const add = dg.filter((d: any) => (d.personaId ? !have.has(d.personaId) : true)).map(normDoelgroep);
          return [...cur, ...add];
        });
      }
      const model = strategie.modelKeuze ? MODEL_OPTS.find((o) => o.v === strategie.modelKeuze)?.l.split(' —')[0] : '';
      const extra = [cs.length ? `${cs.length} spoor(en)` : '', fs.length ? `${fs.length} fase(n)` : '', dg.length ? `${dg.length} doelgroep(en)` : ''].filter(Boolean).join(' + ');
      toast.success('AI-concept ingevuld', filled > 0 ? `Model: ${model || '—'}. ${filled} onderdeel(en) ingevuld${extra ? ` + ${extra}` : ''}. Scherp de keuzes zelf aan.` : (extra ? `${extra} voorgesteld.` : 'Alles was al ingevuld — niets overschreven.'));
    } catch (e: any) {
      toast.error('AI-ondersteuning mislukt', e?.response?.data?.error || 'Probeer het later opnieuw.');
    } finally { setAiLoading(false); }
  };

  const model = content.modelKeuze;
  const showVR = model === 'van-ruler' || model === 'hybride';
  const showMI = model === 'michels' || model === 'hybride';
  const showHY = model === 'hybride';

  const modelBouwsteenGevuld = (showVR && htmlHasText(content.vrVisie)) || (showMI && htmlHasText(content.miOrganisatievraag)) || (showHY && htmlHasText(content.hyVerbindendeLijn));
  const canLock = Boolean(htmlHasText(content.diagnoseKern) && model.trim() && modelBouwsteenGevuld && htmlHasText(content.participatieAanpak));

  const doLock = async () => {
    setLocking(true);
    try {
      const res = await dossierAPI.lockSection(project.id, 'strategie', payload());
      setStatus('locked');
      setLockedAt(res?.section?.lockedAt || new Date().toISOString());
      toast.success('Fase gevalideerd', 'De communicatiestrategie is vastgelegd in het projectdossier.');
    } catch (e: any) {
      toast.error('Valideren mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setLocking(false); }
  };

  const doUnlock = async () => {
    setLocking(true);
    try {
      await dossierAPI.unlockSection(project.id, 'strategie');
      setStatus('open');
      toast.success('Fase heropend', 'Je kunt de communicatiestrategie nu aanpassen en opnieuw valideren.');
    } catch (e: any) {
      toast.error('Heropenen mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setLocking(false); }
  };

  if (loading) return <div style={{ padding: 32, color: '#6b7280' }}>Projectdossier laden…</div>;

  const field = (k: ContentKey, minHeight = 84) => (
    <div key={k} style={cardStyle}>
      <label style={labelStyle}>{META[k].label}</label>
      <div style={hintStyle}>{META[k].hint}</div>
      <RichText value={content[k]} onChange={(html) => update(k, html)} disabled={locked} placeholder="Vul in…" minHeight={minHeight} inklapbaar />
    </div>
  );

  return (
    <div style={{ padding: isMobile ? 16 : 32, maxWidth: 1120, margin: '0 auto' }}>
      <FaseTag nr={4} />
      <h1 style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 700, color: BRAND.groen, letterSpacing: '-0.3px' }}>De communicatiestrategie</h1>
      <p style={{ margin: '0 0 20px', color: '#555', fontSize: 15, lineHeight: 1.6 }}>
        Diagnose-gedreven: eerst de communicatiediagnose, dan een onderbouwde modelkeuze. Op basis van je
        keuze verschijnen de bouwstenen van dat model — het Strategisch Communicatie Frame (Van Ruler),
        het communicatiemodel (Michels) of een hybride. AI stelt voor; jij beslist.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 340px', gap: 20, alignItems: 'start' }}>
        <div style={{ minWidth: 0 }}>

          {/* 1 · Communicatiediagnose */}
          <div style={sectionTitle}>1. Communicatiediagnose</div>
          <div style={{ display: 'grid', gap: 16 }}>{DIAGNOSE.map((k) => field(k))}</div>

          {/* 2 · Modelkeuze */}
          <div style={{ ...sectionTitle, marginTop: 24 }}>2. Strategisch communicatiemodel</div>
          <div style={{ display: 'grid', gap: 16 }}>
            <div style={cardStyle}>
              <label style={labelStyle}>{META.modelKeuze.label}</label>
              <div style={hintStyle}>{META.modelKeuze.hint}</div>
              <select value={content.modelKeuze} disabled={locked} onChange={(e) => update('modelKeuze', e.target.value)} style={selectStyle(locked)}>
                <option value="">— Kies een model —</option>
                {MODEL_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            {field('modelVerantwoording')}
            {field('modelBeperkingen', 60)}
          </div>

          {/* 3 · Bouwstenen van het gekozen model */}
          <div style={{ ...sectionTitle, marginTop: 24 }}>3. Bouwstenen van het model</div>
          {!model && (
            <div style={{ ...cardStyle, color: '#8a90a0', fontSize: 13.5, background: '#fafbfc', borderStyle: 'dashed' }}>
              Kies eerst een model hierboven. Daarna verschijnen hier de bouwstenen van dat model.
            </div>
          )}
          {showHY && (
            <div style={{ ...hintStyle, background: faseKleur(4).soft, border: `1px solid ${faseKleur(4).border}`, borderRadius: 8, padding: '10px 12px', marginBottom: 14, color: '#3f5a4e' }}>
              Bij een hybride model werk je de bouwstenen van <strong>beide</strong> modellen uit — de 8 bouwstenen van het
              Strategisch Communicatie Frame (Van Ruler) én de 3 blokken met 12 bouwstenen van het communicatiemodel (Michels).
              De verbindende lijn onderaan legt uit hoe ze samen één geheel vormen.
            </div>
          )}
          {showVR && (
            <>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1e3a2f', margin: '4px 0 10px' }}>Strategisch Communicatie Frame — Van Ruler</div>
              <div style={{ display: 'grid', gap: 16 }}>{VR.map((k) => field(k))}</div>
            </>
          )}
          {showMI && (
            <>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1e3a2f', margin: showVR ? '20px 0 10px' : '4px 0 10px' }}>Communicatiemodel — Michels</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#8a90a0', textTransform: 'uppercase', letterSpacing: 0.4, margin: '2px 0 8px' }}>Blok 1 · Analyse</div>
              <div style={{ display: 'grid', gap: 16 }}>{MI_ANALYSE.map((k) => field(k))}</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#8a90a0', textTransform: 'uppercase', letterSpacing: 0.4, margin: '18px 0 8px' }}>Blok 2 · Strategie</div>
              <div style={{ display: 'grid', gap: 16 }}>{MI_STRATEGIE.map((k) => field(k))}</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#8a90a0', textTransform: 'uppercase', letterSpacing: 0.4, margin: '18px 0 8px' }}>Blok 3 · Aanpak</div>
              <div style={{ display: 'grid', gap: 16 }}>{MI_AANPAK.map((k) => field(k))}</div>
            </>
          )}
          {showHY && (
            <div style={{ marginTop: 16 }}>{field('hyVerbindendeLijn')}</div>
          )}

          {/* 4 · Communicatiesporen */}
          <div style={{ ...sectionTitle, marginTop: 24 }}>4. Communicatiesporen</div>
          <div style={hintStyle}>Structureer de strategie in sporen (bijv. omgeving, bestuurlijk, publiek, intern, gedrag, uitvoering/hinder) waar dat helpt.</div>
          <div style={{ display: 'grid', gap: 12 }}>
            {sporen.map((s, idx) => (
              <div key={idx} style={{ ...cardStyle, background: '#fafbfc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <input value={s.naam} disabled={locked} onChange={(e) => updateSpoor(idx, 'naam', e.target.value)} placeholder="Naam van het spoor" style={{ ...selectStyle(locked), fontWeight: 700, color: '#1e3a2f' }} />
                  {!locked && <button onClick={() => setSporen((l) => l.filter((_, i) => i !== idx))} style={{ flexShrink: 0, border: 'none', background: 'transparent', color: '#b91c1c', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Verwijderen</button>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                  <div><label style={{ ...labelStyle, fontSize: 12.5 }}>Doel</label><textarea value={s.doel} disabled={locked} onChange={(e) => updateSpoor(idx, 'doel', e.target.value)} style={textareaStyle(locked)} /></div>
                  <div><label style={{ ...labelStyle, fontSize: 12.5 }}>Betrokkenen</label><textarea value={s.betrokkenen} disabled={locked} onChange={(e) => updateSpoor(idx, 'betrokkenen', e.target.value)} style={textareaStyle(locked)} /></div>
                  <div><label style={{ ...labelStyle, fontSize: 12.5 }}>Gewenste beweging/effect</label><textarea value={s.gewensteBeweging} disabled={locked} onChange={(e) => updateSpoor(idx, 'gewensteBeweging', e.target.value)} style={textareaStyle(locked)} /></div>
                  <div><label style={{ ...labelStyle, fontSize: 12.5 }}>Kern van de aanpak</label><textarea value={s.kernAanpak} disabled={locked} onChange={(e) => updateSpoor(idx, 'kernAanpak', e.target.value)} style={textareaStyle(locked)} /></div>
                  <div style={{ gridColumn: isMobile ? 'auto' : '1 / -1' }}><label style={{ ...labelStyle, fontSize: 12.5 }}>Risico’s & randvoorwaarden</label><textarea value={s.risicos} disabled={locked} onChange={(e) => updateSpoor(idx, 'risicos', e.target.value)} style={textareaStyle(locked)} /></div>
                </div>
              </div>
            ))}
            {!locked && <button onClick={() => setSporen((l) => [...l, emptySpoor()])} style={{ padding: '9px 14px', borderRadius: 8, border: `1px dashed ${faseKleur(4).border}`, background: '#fff', color: '#1e3a2f', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>+ Spoor toevoegen</button>}
          </div>

          {/* 5 · Narratief */}
          <div style={{ ...sectionTitle, marginTop: 24 }}>5. Narratief</div>
          <div style={hintStyle}>Het overkoepelende verhaal van het project. De concrete kernboodschap en ondersteunende boodschappen werk je uit in Fase 5.</div>
          <div style={{ display: 'grid', gap: 16 }}>{SHARED_NARRATIEF.map((k) => field(k))}</div>

          {/* 6 · Participatie & afspraken */}
          <div style={{ ...sectionTitle, marginTop: 24 }}>6. Participatie & afspraken</div>
          <div style={{ display: 'grid', gap: 16 }}>{SHARED_PARTICIPATIE.map((k) => field(k))}</div>

          {/* 7 · Fasering & mijlpalen */}
          <div style={{ ...sectionTitle, marginTop: 24 }}>7. Fasering & mijlpalen</div>
          <div style={hintStyle}>Werk de strategie uit per relevante projectfase, met de belangrijke momenten, besluiten en mijlpalen.</div>
          <div style={{ display: 'grid', gap: 12 }}>
            {fasering.map((s, idx) => (
              <div key={idx} style={{ ...cardStyle, background: '#fafbfc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <input value={s.fase} disabled={locked} onChange={(e) => updateFase(idx, 'fase', e.target.value)} placeholder="Projectfase (bv. planvorming, uitvoering, nazorg)" style={{ ...selectStyle(locked), fontWeight: 700, color: '#1e3a2f' }} />
                  {!locked && <button onClick={() => setFasering((l) => l.filter((_, i) => i !== idx))} style={{ flexShrink: 0, border: 'none', background: 'transparent', color: '#b91c1c', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Verwijderen</button>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                  <div><label style={{ ...labelStyle, fontSize: 12.5 }}>Communicatieopgave</label><textarea value={s.opgave} disabled={locked} onChange={(e) => updateFase(idx, 'opgave', e.target.value)} style={textareaStyle(locked)} /></div>
                  <div><label style={{ ...labelStyle, fontSize: 12.5 }}>Belangrijke momenten & mijlpalen</label><textarea value={s.mijlpalen} disabled={locked} onChange={(e) => updateFase(idx, 'mijlpalen', e.target.value)} style={textareaStyle(locked)} /></div>
                  <div><label style={{ ...labelStyle, fontSize: 12.5 }}>Aanpak</label><textarea value={s.aanpak} disabled={locked} onChange={(e) => updateFase(idx, 'aanpak', e.target.value)} style={textareaStyle(locked)} /></div>
                  <div><label style={{ ...labelStyle, fontSize: 12.5 }}>Risico’s & overgangsvoorwaarden</label><textarea value={s.risicos} disabled={locked} onChange={(e) => updateFase(idx, 'risicos', e.target.value)} style={textareaStyle(locked)} /></div>
                </div>
              </div>
            ))}
            {!locked && <button onClick={() => setFasering((l) => [...l, emptyFase()])} style={{ padding: '9px 14px', borderRadius: 8, border: `1px dashed ${faseKleur(4).border}`, background: '#fff', color: '#1e3a2f', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>+ Projectfase toevoegen</button>}
          </div>

          {/* 8 · Risico's & monitoring */}
          <div style={{ ...sectionTitle, marginTop: 24 }}>8. Risico’s, kansen & monitoring</div>
          <div style={{ display: 'grid', gap: 16 }}>{SHARED_RISICO.map((k) => field(k))}</div>

          {/* Strategie per doelgroep */}
          {doelgroepen.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div style={sectionTitle}>Strategie per doelgroep</div>
              <div style={hintStyle}>Concreet per persona: doel, benadering, participatieniveau en middelen. AI stelt voor; jij scherpt aan.</div>
              <div style={{ display: 'grid', gap: 12 }}>
                {doelgroepen.map((d, idx) => (
                  <div key={d.personaId || idx} style={{ ...cardStyle, background: '#fafbfc' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1e3a2f', marginBottom: 10 }}>{d.persona || 'Doelgroep'}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                      <div><label style={{ ...labelStyle, fontSize: 12.5 }}>Doel (kennis / houding / gedrag)</label><textarea value={d.doel} disabled={locked} onChange={(e) => updateDoelgroep(idx, 'doel', e.target.value)} style={textareaStyle(locked, 60)} /></div>
                      <div><label style={{ ...labelStyle, fontSize: 12.5 }}>Benadering (toon, kanalen, frequentie)</label><textarea value={d.benadering} disabled={locked} onChange={(e) => updateDoelgroep(idx, 'benadering', e.target.value)} style={textareaStyle(locked, 60)} /></div>
                      <div>
                        <label style={{ ...labelStyle, fontSize: 12.5 }}>Participatieniveau (Factor C)</label>
                        <select value={d.participatieniveau || ''} disabled={locked} onChange={(e) => updateDoelgroep(idx, 'participatieniveau', e.target.value)} style={selectStyle(locked)}>
                          <option value="">—</option>
                          {PARTICIPATIE.map((p) => <option key={p} value={p}>{p}</option>)}
                          {d.participatieniveau && !PARTICIPATIE.includes(d.participatieniveau) && (<option value={d.participatieniveau}>{d.participatieniveau}</option>)}
                        </select>
                      </div>
                      <div><label style={{ ...labelStyle, fontSize: 12.5 }}>Middelen</label><textarea value={d.middelen} disabled={locked} onChange={(e) => updateDoelgroep(idx, 'middelen', e.target.value)} style={textareaStyle(locked, 60)} /></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!locked && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={saveDraft} disabled={saving} style={{ padding: '9px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Opslaan…' : 'Concept opslaan'}
              </button>
            </div>
          )}
        </div>

        {/* RECHTERKOLOM — AI-ondersteuning */}
        <div>
          <div style={{ background: faseKleur(4).soft, border: `1px solid ${faseKleur(4).border}`, borderRadius: 12, padding: 18, position: isMobile ? 'static' : 'sticky', top: 16 }}>
            <div style={{ ...labelStyle, marginBottom: 2 }}>AI-ondersteuning</div>
            <div style={hintStyle}>AI stelt een conceptstrategie voor: diagnose, onderbouwde modelkeuze en de bouwstenen van dát model (Van Ruler / Michels / hybride), plus sporen, fasering, narratief en aanpak per doelgroep — op basis van het dossier en de documenten. Jij beslist.</div>
            <button onClick={runAssist} disabled={aiLoading || locked} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '10px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, border: 'none', background: '#1e3a2f', color: '#fff', cursor: aiLoading || locked ? 'default' : 'pointer', opacity: aiLoading || locked ? 0.6 : 1 }}>
              {aiLoading ? 'AI stelt op…' : 'Conceptstrategie voorstellen'}
            </button>
          </div>
        </div>
      </div>

      <LockBar status={status} onLock={doLock} onUnlock={doUnlock} lockedAt={lockedAt} canLock={canLock} busy={locking} faseLabel="Communicatiestrategie" canClear={canClear} onClear={doClear} clearing={clearing} />
      {!canLock && !locked && (
        <div style={{ marginTop: 8, fontSize: 12.5, color: '#8a90a0', textAlign: 'right' }}>
          Vul minimaal de kern van het vraagstuk, de modelkeuze + de eerste bouwsteen van dat model, en de participatie-aanpak in om te kunnen valideren.
        </div>
      )}
    </div>
  );
};
